import { useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Select,
  Stack, Switch, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import TuneIcon from '@mui/icons-material/Tune';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { ApiFallo, BibliotecaDestino, Conversion } from '../lib/api.js';
import { etiquetaEpisodio, nombreTemporada } from '@film/shared';
import { EditorPistas } from '../components/EditorPistas.js';
import { EditorEpisodio } from '../components/EditorEpisodio.js';
import { RegistroConversion } from '../components/RegistroConversion.js';
import { Reproductor } from '../components/Reproductor.js';
import { PanelCola } from '../components/ColaConversion.js';
import { formatearTamano } from '../lib/eventos.js';
import { SelectorOrigen, contarOrigenes, origenInicial, pestanaDe } from '../components/SelectorOrigen.js';
import type { OrigenDescarga } from '../lib/api.js';

/** De dónde salió el fichero que espera. Importa porque cambia lo que ocupa en disco: una
 *  conversión es un fichero nuevo —el original sigue aparte, sembrando— y un enlace es el
 *  mismo dato con otro nombre. */
function describirTipo(t: Conversion['tipo'], origen: Conversion['origen']): { texto: string; nota: string } {
  // Una fila de biblioteca (D-28) no tiene aMule detrás: su original es el fichero de Plex,
  // y al colocarla en su biblioteca lo sustituye.
  const deBiblioteca = origen === 'biblioteca';
  switch (t) {
    case 'transcoded':
      return {
        texto: 'Recodificada',
        nota: deBiblioteca
          ? 'Fichero nuevo, convertido desde el que ya estaba en la biblioteca: al moverla allí lo sustituirá.'
          : 'Fichero nuevo. El original sigue en descargas compartiéndose hasta que la recogida lo retire.',
      };
    case 'remux':
      return {
        texto: 'Remuxeada',
        nota: deBiblioteca
          ? 'Solo cambió el contenedor, sin perder calidad, desde el fichero que ya estaba en la biblioteca: al moverla allí lo sustituirá.'
          : 'Solo cambió el contenedor, sin perder calidad. El original sigue compartiéndose aparte.',
      };
    case 'linked':
      return {
        texto: 'Sin convertir',
        nota: 'Es un enlace duro del fichero de aMule: el mismo dato con otro nombre, no ocupa el doble.',
      };
    case 'copied':
      return {
        texto: 'Sin convertir (copiada)',
        nota:
          'No había nada que convertir, pero tampoco se pudo enlazar: es una copia del fichero de ' +
          'aMule y ocupa el doble. Suelen ser dos montajes separados del mismo disco; el ' +
          'diagnóstico de Ajustes lo dice.',
      };
  }
}

/** La raíz de una biblioteca, con la barra del final quitada: sin eso, una biblioteca
 *  configurada como `/medios/cine/` no casaría con ninguna ruta guardada. */
function raizDe(b: BibliotecaDestino | undefined): string | undefined {
  return b?.localPath.replace(/\/+$/, '');
}

/** ¿La copia que dejó una colocación anterior de esta misma película está DENTRO de esa
 *  biblioteca? Si lo está, moverla ahí la sustituye; si no, van a quedar dos.
 *
 *  Se compara con la barra puesta: sin ella `/medios/cine` daría por buena una copia que en
 *  realidad está en `/medios/cine-viejo`. */
function sustituyeLaPrevia(c: Conversion, raiz: string | undefined): boolean {
  return !!c.colocadaEn && !!raiz && c.colocadaEn.startsWith(`${raiz}/`);
}

/** Dónde va a acabar, para enseñarlo ANTES de mover. Lo normal es la raíz más el nombre;
 *  si sustituye a un fichero de esa misma biblioteca, la película va a la carpeta de ese
 *  fichero (D-28), que es lo que hace `rutaDeColocacion` en el servidor. Un episodio lleva
 *  sus carpetas y no sigue al fichero sustituido. */
function rutaDestino(c: Conversion, b: BibliotecaDestino, sustituye: boolean): string {
  if (sustituye && c.kind !== 'show' && c.colocadaEn && c.nombreDestino) {
    const carpeta = c.colocadaEn.slice(0, c.colocadaEn.lastIndexOf('/'));
    return `${carpeta}/${c.nombreDestino.split('/').pop()}`;
  }
  return `${b.localPath}/${c.nombreDestino}`;
}

/** Se puede elegir para mover en bloque. Lo que ya se está colocando no —está en marcha— y
 *  lo que no está en disco tampoco, que es el mismo motivo por el que su botón está gris. */
function esSeleccionable(c: Conversion): boolean {
  // Y lo que no se puede nombrar tampoco: un episodio sin temporada no tiene adónde ir
  // hasta que alguien se la ponga (D-27).
  return c.existe && c.status !== 'importing' && !!c.nombreDestino;
}

/** Las bibliotecas a las que puede ir ESTE fichero: las de su tipo (D-27). Un episodio en
 *  una biblioteca de películas es un fichero con el nombre bueno en el sitio malo. */
function bibliotecasPara(kind: Conversion['kind'], bibliotecas: BibliotecaDestino[]): BibliotecaDestino[] {
  return bibliotecas.filter((b) => b.type === kind);
}

const nombreTipo = (kind: Conversion['kind']) => (kind === 'show' ? 'series' : 'películas');

/** Los episodios de una misma serie y temporada, seguidos y con una cabecera (D-27). Cada
 *  uno conserva su fila —su ruta, sus avisos, sus pistas—; la cabecera solo los presenta y
 *  deja marcarlos de una vez para moverlos juntos. Un episodio solo no forma grupo. */
function agruparConversiones(items: Conversion[]): ({ tipo: 'suelta'; c: Conversion } | { tipo: 'temporada'; items: Conversion[] })[] {
  const clave = (c: Conversion) => (c.kind === 'show' ? `${c.title}|${c.year ?? ''}|${c.season ?? 'sin'}` : null);
  const grupos = new Map<string, Conversion[]>();
  for (const c of items) {
    const k = clave(c);
    if (k) grupos.set(k, [...(grupos.get(k) ?? []), c]);
  }
  const salida: ReturnType<typeof agruparConversiones> = [];
  const pintados = new Set<string>();
  for (const c of items) {
    const k = clave(c);
    const grupo = k ? grupos.get(k) : undefined;
    if (!grupo || grupo.length < 2) {
      salida.push({ tipo: 'suelta', c });
      continue;
    }
    if (pintados.has(k!)) continue;
    pintados.add(k!);
    salida.push({ tipo: 'temporada', items: grupo });
  }
  return salida;
}

function CabeceraTemporada({ items, alMarcar }: { items: Conversion[]; alMarcar: ((ids: string[]) => void) | null }) {
  const primera = items[0]!;
  const movibles = items.filter(esSeleccionable);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap', px: 0.5, pt: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {primera.title}
        {primera.year && <Typography component="span" color="text.secondary"> ({primera.year})</Typography>}
        <Typography component="span" color="text.secondary"> · {nombreTemporada(primera.season)}</Typography>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {items.length} episodios esperando
      </Typography>
      {alMarcar && movibles.length > 1 && (
        <Button size="small" variant="text" onClick={() => alMarcar(movibles.map((c) => c.downloadId))}>
          Marcar los {movibles.length} para moverlos juntos
        </Button>
      )}
    </Stack>
  );
}

/** Lo que la fila deja hacer cuando la pantalla ofrece mover en bloque. `destino` manda
 *  sobre el desplegable propio mientras está marcada: la ruta que se enseña y los avisos de
 *  sustituir o duplicar tienen que ser los del movimiento que se va a hacer de verdad, no
 *  los de una biblioteca que ya no pinta nada. */
interface SeleccionDeFila {
  marcada: boolean;
  alMarcar: (v: boolean) => void;
  /** La biblioteca elegida arriba, solo mientras está marcada. */
  destino: string | null;
}

function FilaConversion({
  c,
  bibliotecas: todasLasBibliotecas,
  onCambio,
  seleccion,
}: {
  c: Conversion;
  bibliotecas: BibliotecaDestino[];
  onCambio: () => void;
  /** `null` cuando no hay nada que seleccionar: con una sola película en la carpeta, una
   *  casilla y una barra de acciones sobran. */
  seleccion: SeleccionDeFila | null;
}) {
  // Solo las de su tipo: un episodio no se ofrece a una biblioteca de películas (D-27).
  const bibliotecas = bibliotecasPara(c.kind, todasLasBibliotecas);
  const esSerie = c.kind === 'show';
  /** Corrigiendo la temporada y el episodio a mano. Se abre solo si falta alguno. */
  const [editandoEpisodio, setEditandoEpisodio] = useState(false);
  // Si esto es una reconversión de algo que YA se colocó, la biblioteca donde está esa
  // copia manda sobre la sugerida de Ajustes: es la única a la que mover sustituye lo de
  // antes en vez de dejar dos ficheros de la misma película.
  const [destinoPropio, setDestinoPropio] = useState(
    () =>
      bibliotecas.find((b) => b.id === c.colocadaEnBiblioteca)?.id ??
      bibliotecas.find((b) => b.sugerida)?.id ??
      bibliotecas[0]?.id ??
      '',
  );
  // Marcada, manda la biblioteca de la barra: es la que va a recibir esta película.
  const marcada = seleccion?.marcada ?? false;
  const destino = (marcada ? seleccion?.destino : null) ?? destinoPropio;
  const [editando, setEditando] = useState(false);
  const [viendoRegistro, setViendoRegistro] = useState(false);
  /** Abierta en el reproductor (D-26): lo que se está a punto de colocar, tal cual quedó. */
  const [viendo, setViendo] = useState(false);
  const [aEliminar, setAEliminar] = useState(false);
  const [borrarOriginal, setBorrarOriginal] = useState(false);

  const colocar = useMutation({
    mutationFn: () => api.colocar(c.downloadId, destino),
    onSuccess: onCambio,
  });

  const eliminar = useMutation({
    mutationFn: () => api.eliminarConversion(c.downloadId, borrarOriginal),
    onSuccess: () => {
      setAEliminar(false);
      setBorrarOriginal(false);
      onCambio();
    },
  });

  const cerrarEliminar = () => {
    setAEliminar(false);
    setBorrarOriginal(false);
    eliminar.reset();
  };

  // Sin original no hay a qué volver: la casilla sobra y ofrecerla sería ofrecer un fallo.
  const hayOriginal = !c.originalReaped && !!c.originalPath;

  // Moviéndose AHORA. El servidor contesta en cuanto arranca —copiar a otra biblioteca son
  // varios gigas— así que quien manda mientras dura es el estado de la fila, no el
  // `isPending` de la petición, que ya volvió. Sin esto la película desaparecía de la lista
  // en el primer refresco y el botón se quedaba gris sin decir por qué.
  const moviendo = c.status === 'importing' || colocar.isPending;
  // En `importing` pero sin turno todavía: las colocaciones se hacen de una en una, así que
  // decir «moviéndose» de todas las de una tacada sería mentir en todas menos una.
  const esperandoTurno = c.status === 'importing' && !c.moviendoAhora;

  const tipo = describirTipo(c.tipo, c.origen);
  const biblioteca = bibliotecas.find((b) => b.id === destino);

  // Ya hubo una colocación y su fichero sigue en la biblioteca: esto es una reconversión.
  // Mover a la MISMA biblioteca lo sustituye —es la única excepción a «no se sobrescribe
  // nunca», y solo vale sobre la copia de esta misma película—; a otra, deja dos, y eso hay
  // que decirlo ANTES de mover, que es cuando todavía se puede cambiar de idea.
  const sustituye = sustituyeLaPrevia(c, raizDe(biblioteca));
  const duplicaria = !!c.colocadaEn && !sustituye;

  /** Por qué no se puede mover, o `null` si sí se puede. Un botón gris que no dice nada
   *  obliga a adivinar; cada uno de estos casos tiene un arreglo concreto. */
  const motivoSinMover = esperandoTurno
    ? 'Está en la fila para moverse: las colocaciones se hacen de una en una.'
    : moviendo
      ? 'Se está moviendo ahora mismo. Al terminar cambia de estado sola.'
      : marcada
        ? 'Está marcada para moverla con las demás: se mueve desde el botón de arriba.'
        : !c.nombreDestino
          ? `${c.motivoNoColocable ?? 'No se puede nombrar este fichero.'} Pon la temporada y el episodio aquí debajo.`
          : !bibliotecas.length
            ? todasLasBibliotecas.length
              ? `No hay ninguna biblioteca de ${nombreTipo(c.kind)} con ruta local. Se rellena en Ajustes → Servidores Plex.`
              : 'No hay ninguna biblioteca con ruta local configurada. Se rellena en Ajustes → Servidores Plex.'
            : !destino
              ? 'Elige a qué biblioteca va.'
              : !c.existe
                ? `El fichero ya no está en ${c.path}: no hay nada que mover.`
                : null;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' } }}>
        <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
          {seleccion && (
            <Checkbox
              sx={{ mt: -0.5, ml: -1.25 }}
              checked={marcada}
              onChange={(e) => seleccion.alMarcar(e.target.checked)}
              slotProps={{ input: { 'aria-label': `Seleccionar ${c.title}` } }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
              {c.title}
              {c.year && <Typography component="span" color="text.secondary"> ({c.year})</Typography>}
              {/* Un episodio se distingue de sus hermanos por esto (D-27). */}
              {esSerie && (
                <Typography component="span" color={c.nombreDestino ? 'text.secondary' : 'warning.main'}>
                  {' · '}{etiquetaEpisodio(c.season, c.episode, c.episodeEnd) ?? 'sin numerar'}
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {c.fileName}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
              <Tooltip title={tipo.nota}>
                <Chip size="small" variant="outlined" label={tipo.texto} />
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {formatearTamano(c.sizeBytes)}
              </Typography>
              {c.stagedAt && (
                <Typography variant="caption" color="text.secondary">
                  · esperando desde {new Date(c.stagedAt).toLocaleString('es-ES')}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>

        {/* A 375 px estos cuatro controles no caben en fila y el desplegable de biblioteca
            —el que decide adónde va la película— se salía de la tarjeta. Envuelve, y el
            desplegable ocupa el ancho que le quede en vez de un mínimo fijo (X-3). */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
        >
          {/* Verla antes de moverla: es la última oportunidad de comprobar que el MP4 lleva
              el audio y los subtítulos que debía sin tener que ponerla en Plex. Se ofrece
              también el original, para comparar. */}
          <Tooltip title="Ver la conversión en el navegador, con sus pistas de audio y subtítulos, antes de colocarla">
            <span>
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => setViendo(true)}
                disabled={moviendo}
              >
                Ver
              </Button>
            </span>
          </Tooltip>
          {/* Las pistas se editan sobre el ORIGINAL de aMule, que sigue en su carpeta: por
              eso se puede corregir la conversión sin volver a descargar nada. Retirado el
              original, no hay de dónde sacarlas: se desactiva DICIENDO por qué, en vez de
              dejar que el editor falle al abrirse. */}
          <Tooltip
            title={
              c.originalReaped
                ? 'El original ya no está en aMule: para rehacer la conversión hay que volver a bajarlo desde Descargas.'
                : 'Ver y corregir las pistas: idioma, título, orden y cuáles entran'
            }
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={() => setEditando(true)}
                disabled={moviendo || c.originalReaped}
              >
                Pistas
              </Button>
            </span>
          </Tooltip>
          {/* Está convertida y esperando, pero el registro sigue teniendo por qué mirarse:
              lo que se descartó, lo que avisó el OCR y cuánto tardó están ahí. */}
          <Tooltip title="Ver el registro de la conversión: qué se decidió y qué dijo ffmpeg">
            <IconButton onClick={() => setViendoRegistro(true)}>
              <ArticleOutlinedIcon />
            </IconButton>
          </Tooltip>
          {/* Marcada, el desplegable enseña la biblioteca de la barra y no se toca: la
              película va adonde diga el movimiento en bloque, y dos sitios distintos donde
              elegir lo mismo es la forma más rápida de mover algo adonde no querías. */}
          <Select
            size="small"
            value={destino}
            onChange={(e) => setDestinoPropio(e.target.value)}
            sx={{ minWidth: { xs: 0, sm: 260 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
            disabled={!bibliotecas.length || moviendo || marcada}
          >
            {bibliotecas.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.title}
                {b.sugerida ? ' · sugerida' : ''}
              </MenuItem>
            ))}
          </Select>
          {/* Deshabilitado dentro de un `span`: MUI no dispara el tooltip de un botón
              apagado, y un botón gris sin explicación es precisamente lo que hay que
              evitar aquí — cada motivo de que no se pueda mover tiene su arreglo. */}
          <Tooltip title={motivoSinMover ?? 'Mueve el fichero a la biblioteca elegida'}>
            <span>
              <Button
                variant="contained"
                startIcon={moviendo ? <CircularProgress size={16} color="inherit" /> : <DriveFileMoveIcon />}
                disabled={!!motivoSinMover}
                onClick={() => colocar.mutate()}
              >
                {esperandoTurno ? 'En espera…' : moviendo ? 'Moviendo…' : 'Mover'}
              </Button>
            </span>
          </Tooltip>
          {/* Descartar va al margen y sin relleno: es lo contrario de lo que se viene a
              hacer a esta pantalla, y confirmarlo se hace en el diálogo, no aquí. */}
          <Tooltip title="Eliminar de conversiones">
            <span>
              <IconButton
                color="error"
                onClick={() => setAEliminar(true)}
                disabled={moviendo}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Adónde va exactamente, ANTES de mover. Enseñarlo después no sirve para decidir.
          Una película va a la raíz de la biblioteca; un episodio, a la carpeta de su serie y
          su temporada (D-27). Y si sustituye a un fichero que ya estaba en esa biblioteca,
          va a la carpeta de ese fichero (D-28): es la misma regla que aplica el servidor. */}
      {biblioteca && c.nombreDestino && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
          >
            {rutaDestino(c, biblioteca, sustituye)}
          </Typography>
          {esSerie && !editandoEpisodio && (
            <Button size="small" variant="text" onClick={() => setEditandoEpisodio(true)} disabled={moviendo}>
              Corregir temporada o episodio
            </Button>
          )}
        </Stack>
      )}

      {/* Sin temporada o sin número no hay nombre posible, y no se inventa (D-27): se pide. */}
      {esSerie && !c.nombreDestino && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <AlertTitle>No se puede colocar todavía</AlertTitle>
          {c.motivoNoColocable} Plex necesita la temporada y el número para saber dónde va, y
          adivinarlos dejaría un capítulo en el sitio equivocado. Ponlos aquí:
          <EditorEpisodio
            downloadId={c.downloadId}
            season={c.season}
            episode={c.episode}
            episodeEnd={c.episodeEnd}
            onGuardado={onCambio}
          />
        </Alert>
      )}
      {esSerie && c.nombreDestino && editandoEpisodio && (
        <EditorEpisodio
          downloadId={c.downloadId}
          season={c.season}
          episode={c.episode}
          episodeEnd={c.episodeEnd}
          onGuardado={() => {
            setEditandoEpisodio(false);
            onCambio();
          }}
          onCancelar={() => setEditandoEpisodio(false)}
        />
      )}

      {/* Que esto sustituye a algo, o que va a dejar dos copias, se dice ANTES de mover:
          después ya no hay nada que decidir. */}
      {sustituye && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {c.origen === 'biblioteca'
            ? 'Viene de la revisión de la biblioteca: es la conversión de un fichero que ya estaba en ella. Al moverla sustituirá a '
            : 'Es una reconversión de una película que ya estaba colocada. Al moverla sustituirá a '}
          <code>{c.colocadaEn}</code>
          {c.origen === 'biblioteca'
            ? ', el fichero de antes, y es lo único que puede sobrescribir: cualquier otro fichero que ocupara ese nombre pararía la colocación, como siempre.'
            : ', la copia que dejó la vez anterior, y es lo único que puede sobrescribir: cualquier otro fichero que ocupara ese nombre pararía la colocación, como siempre.'}
        </Alert>
      )}

      {duplicaria && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <AlertTitle>Vas a dejar dos copias de la misma película</AlertTitle>
          Esta ya está colocada en <code>{c.colocadaEn}</code>, y la biblioteca elegida es
          otra: ese fichero se queda donde está y Plex acabará con las dos. Elige esa misma
          biblioteca para que la nueva sustituya a la anterior, o borra la vieja a mano.
        </Alert>
      )}

      {c.status === 'importing' && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 1 }}>
          {esperandoTurno
            ? 'Esperando turno para moverse. Las colocaciones se hacen de una en una: entre ' +
              'discos distintos cada una copia la película entera, y lanzarlas todas a la vez ' +
              'satura el disco y las hace más lentas todas.'
            : 'Moviéndose a la biblioteca. Si está en otro disco hay que copiar varios gigas y ' +
              'tarda unos minutos; puedes irte de esta pantalla, la fila cambia de estado sola.'}
        </Alert>
      )}

      {!c.existe && c.status !== 'importing' && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          El fichero ya no está en <code>{c.path}</code>. Si lo has movido a mano, ya está
          colocado; si no, reintenta la conversión desde Descargas.
        </Alert>
      )}

      {c.errorDetail && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          El intento anterior falló: {c.errorDetail}
        </Alert>
      )}

      {colocar.isError && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => colocar.reset()}>
          {(colocar.error as ApiFallo).message}
        </Alert>
      )}

      {viendoRegistro && (
        <RegistroConversion
          downloadId={c.downloadId}
          titulo={c.title}
          abierto={viendoRegistro}
          onCerrar={() => setViendoRegistro(false)}
        />
      )}

      {viendo && (
        <Reproductor
          downloadId={c.downloadId}
          titulo={c.title}
          abierto={viendo}
          onCerrar={() => setViendo(false)}
        />
      )}

      {editando && (
        <EditorPistas
          downloadId={c.downloadId}
          titulo={c.title}
          abierto={editando}
          onCerrar={() => setEditando(false)}
          onReconvertida={onCambio}
        />
      )}

      {/* Borrar el fichero de conversiones se deshace reconvirtiendo; borrar el original,
          no. Por eso son dos decisiones y no una, y la segunda dice qué cuesta. */}
      <Dialog open={aEliminar} onClose={cerrarEliminar} fullWidth maxWidth="sm">
        <DialogTitle>Eliminar de conversiones</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se borra <code>{c.fileName}</code> ({formatearTamano(c.sizeBytes)}) de la carpeta
            de conversiones. «{c.title}» no entra en ninguna biblioteca.
          </DialogContentText>

          <Alert severity="info" sx={{ mt: 2 }}>
            {c.tipo === 'linked'
              ? 'Este fichero es un enlace duro del original: borrarlo solo le quita un nombre, ' +
                'la película sigue entera en aMule.'
              : hayOriginal
                ? 'El original sigue en descargas compartiéndose, así que se puede volver a ' +
                  'convertir desde Descargas sin bajar un byte.'
                : 'El original ya no está en aMule: para volver a tenerla habría que descargarla ' +
                  'otra vez.'}
          </Alert>

          <Tooltip
            title={hayOriginal ? '' : 'El original ya no está en la carpeta de descargas.'}
          >
            <FormControlLabel
              sx={{ mt: 2 }}
              disabled={!hayOriginal}
              control={
                <Switch
                  checked={borrarOriginal}
                  onChange={(e) => setBorrarOriginal(e.target.checked)}
                />
              }
              label="Borrar también el original de aMule"
            />
          </Tooltip>

          {borrarOriginal && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {c.tipo === 'linked'
                ? 'Original y copia son EL MISMO fichero: al borrarlo se pierde la película.'
                : 'Se pierde la película entera.'}{' '}
              Deja de compartirse en aMule y para recuperarla habría que descargarla de nuevo.
            </Alert>
          )}

          {eliminar.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              No se pudo eliminar: {(eliminar.error as ApiFallo).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarEliminar}>Volver</Button>
          <Button color="error" disabled={eliminar.isPending} onClick={() => eliminar.mutate()}>
            {eliminar.isPending ? 'Eliminando…' : borrarOriginal ? 'Eliminar todo' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/** Mover varias a la misma biblioteca de una vez.
 *
 *  Es la misma decisión de D-15 —a qué biblioteca va— tomada para un puñado de películas
 *  en vez de para una: quien acaba de convertir seis capítulos de la misma noche no tiene
 *  por qué elegir seis veces lo mismo. Lo que NO hace es decidir por nadie: la biblioteca
 *  se elige aquí, y lo que va a pasar con cada película sigue estando escrito en su fila,
 *  con su ruta y sus avisos, antes de pulsar.
 *
 *  El servidor los hace de uno en uno; aquí se manda una sola petición porque seis
 *  peticiones sueltas los habrían lanzado los seis a la vez. */
function BarraSeleccion({
  movibles,
  marcadas,
  bibliotecas: todasLasBibliotecas,
  destino,
  alElegirDestino,
  alMarcarTodas,
  alDejarSolo,
  onCambio,
}: {
  movibles: Conversion[];
  marcadas: Conversion[];
  bibliotecas: BibliotecaDestino[];
  destino: string;
  alElegirDestino: (id: string) => void;
  alMarcarTodas: (v: boolean) => void;
  /** Deja marcadas exactamente esas: es como se quedan señaladas las que no arrancaron. */
  alDejarSolo: (ids: string[]) => void;
  onCambio: () => void;
}) {
  const mover = useMutation({
    mutationFn: () =>
      api.colocarVarias(
        marcadas.map((c) => c.downloadId),
        destino,
      ),
    onSuccess: (r) => {
      // Se desmarca lo que arrancó y se deja marcado lo que no: así queda a la vista lo que
      // falta, y reintentarlo —quizá contra otra biblioteca— es volver a pulsar.
      alDejarSolo(r.fallos.map((f) => f.downloadId));
      onCambio();
    },
  });

  // Películas y episodios van a bibliotecas distintas (D-27): con las marcadas de un solo
  // tipo se ofrecen las de ese tipo; mezcladas, no hay biblioteca que valga para todas.
  const tipos = [...new Set(marcadas.map((c) => c.kind))];
  const tipo = tipos.length === 1 ? tipos[0]! : null;
  const bibliotecas = tipo ? bibliotecasPara(tipo, todasLasBibliotecas) : todasLasBibliotecas;
  const biblioteca = todasLasBibliotecas.find((b) => b.id === destino);
  const raiz = raizDe(biblioteca);
  // Cada fila ya avisa por su cuenta de si sustituye o duplica; aquí se cuentan porque con
  // doce marcadas nadie va a leerlas una a una antes de pulsar.
  const duplicarian = marcadas.filter((c) => !!c.colocadaEn && !sustituyeLaPrevia(c, raiz));
  const sustituyen = marcadas.filter((c) => sustituyeLaPrevia(c, raiz));
  const nombreDe = (id: string) => movibles.find((c) => c.downloadId === id)?.title ?? id;

  const motivo = !todasLasBibliotecas.length
    ? 'No hay ninguna biblioteca con ruta local configurada. Se rellena en Ajustes → Servidores Plex.'
    : tipos.length > 1
      ? 'Hay películas y episodios marcados a la vez, y van a bibliotecas distintas. Marca solo unas u otros.'
      : !destino
        ? 'Elige a qué biblioteca van.'
        : !marcadas.length
          ? 'Marca al menos una película.'
          : biblioteca && tipo && biblioteca.type !== tipo
            ? `«${biblioteca.title}» es de ${nombreTipo(biblioteca.type)} y lo marcado son ${tipo === 'show' ? 'episodios' : 'películas'}. Elige una de ${nombreTipo(tipo)}.`
            : null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flex: 1, minWidth: 0 }}>
          <Checkbox
            checked={marcadas.length === movibles.length && movibles.length > 0}
            indeterminate={marcadas.length > 0 && marcadas.length < movibles.length}
            onChange={(e) => alMarcarTodas(e.target.checked)}
            slotProps={{ input: { 'aria-label': 'Seleccionar todas' } }}
            disabled={mover.isPending}
          />
          <Typography variant="body2" color={marcadas.length ? 'text.primary' : 'text.secondary'}>
            {marcadas.length
              ? `${marcadas.length} de ${movibles.length} seleccionadas`
              : `Selecciona para mover varias a la vez (${movibles.length} disponibles)`}
          </Typography>
        </Stack>

        <Select
          size="small"
          value={destino}
          onChange={(e) => alElegirDestino(e.target.value)}
          sx={{ minWidth: { xs: 0, sm: 260 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          disabled={!bibliotecas.length || mover.isPending}
        >
          {bibliotecas.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.title}
              {b.sugerida ? ' · sugerida' : ''}
            </MenuItem>
          ))}
        </Select>

        <Tooltip title={motivo ?? `Mueve las ${marcadas.length} marcadas a «${biblioteca?.title}»`}>
          <span>
            <Button
              variant="contained"
              startIcon={
                mover.isPending ? <CircularProgress size={16} color="inherit" /> : <DriveFileMoveIcon />
              }
              disabled={!!motivo || mover.isPending}
              onClick={() => mover.mutate()}
            >
              {mover.isPending ? 'Moviendo…' : `Mover ${marcadas.length || ''}`.trim()}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Lo mismo que avisa cada fila, contado de una vez: con doce marcadas, el aviso que
          hay que leer antes de pulsar es cuántas van a dejar una copia de más. */}
      {duplicarian.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <AlertTitle>
            {duplicarian.length === 1
              ? 'Una de las marcadas va a quedar duplicada'
              : `${duplicarian.length} de las marcadas van a quedar duplicadas`}
          </AlertTitle>
          Ya están colocadas en otra biblioteca y esos ficheros se quedan donde están, así que
          Plex acabará con las dos copias: {duplicarian.map((c) => c.title).join(', ')}.
        </Alert>
      )}

      {sustituyen.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {sustituyen.length === 1
            ? 'Una de las marcadas es una reconversión y sustituirá a la copia que dejó la vez anterior en esta misma biblioteca'
            : `${sustituyen.length} de las marcadas son reconversiones y sustituirán a la copia que dejaron la vez anterior en esta misma biblioteca`}
          : {sustituyen.map((c) => c.title).join(', ')}.
        </Alert>
      )}

      {/* Que arranquen nueve de diez es lo normal, así que el resultado no es «bien» o
          «mal»: es la lista de las que no, con su motivo y su nombre. Siguen marcadas. */}
      {mover.data && mover.data.fallos.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={() => mover.reset()}>
          <AlertTitle>
            {mover.data.arrancadas.length
              ? `Se movieron ${mover.data.arrancadas.length}; ${mover.data.fallos.length} no`
              : 'No se movió ninguna'}
          </AlertTitle>
          <Stack component="ul" sx={{ pl: 2.5, m: 0 }}>
            {mover.data.fallos.map((f) => (
              <li key={f.downloadId}>
                <strong>{nombreDe(f.downloadId)}</strong>: {f.problema}
              </li>
            ))}
          </Stack>
        </Alert>
      )}

      {mover.isError && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => mover.reset()}>
          {(mover.error as ApiFallo).message}
        </Alert>
      )}
    </Paper>
  );
}

/** La carpeta de conversiones: lo que ya está listo y espera a que alguien decida a qué
 *  biblioteca va (D-15). Nada se mueve solo. */
export function Conversiones() {
  const qc = useQueryClient();
  const { data, error } = useQuery({
    queryKey: ['conversiones'],
    queryFn: api.conversiones,
    refetchInterval: 30_000,
  });

  // Lo marcado se guarda por id y no por posición: la lista se refresca sola cada 30 s y
  // con cada evento, y una película que entra o sale habría corrido las demás.
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const [destinoMasivo, setDestinoMasivo] = useState('');
  // Lo descargado de HispaShare y lo convertido desde la biblioteca (D-28), en pestañas.
  const [origen, setOrigen] = useState<OrigenDescarga | null>(null);

  // Colocar cambia el estado de la descarga, así que las dos listas se quedan viejas a la vez.
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['conversiones'] });
    void qc.invalidateQueries({ queryKey: ['descargas'] });
  };

  if (error) return <Alert severity="error">No se pudo leer la carpeta de conversiones.</Alert>;

  const todas = data?.items ?? [];
  const cuentas = contarOrigenes(todas);
  const pestana = origen ?? origenInicial(cuentas);
  const items = todas.filter((c) => pestanaDe(c) === pestana);
  const bibliotecas = data?.libraries ?? [];

  // Lo que de verdad se puede mover en bloque. Se recalcula de la lista de ahora, así que
  // lo que se colocó o desapareció mientras tanto deja de contar sin tener que limpiar nada.
  const movibles = items.filter(esSeleccionable);
  const marcadas = movibles.filter((c) => marcados.has(c.downloadId));

  // La biblioteca de la barra: la sugerida en Ajustes, mientras nadie elija otra. No se
  // preselecciona la de una colocación anterior como en las filas —eso es de cada
  // película— porque aquí lo que se elige vale para todas las marcadas.
  const destino = destinoMasivo || bibliotecas.find((b) => b.sugerida)?.id || bibliotecas[0]?.id || '';

  const marcar = (id: string, v: boolean) =>
    setMarcados((antes) => {
      const s = new Set(antes);
      if (v) s.add(id);
      else s.delete(id);
      return s;
    });

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">Conversiones</Typography>
        <Typography variant="body2" color="text.secondary">
          Lo que ya está convertido y esperando. Nada entra en una biblioteca solo: eliges tú
          a cuál va cada película.
        </Typography>
      </Box>

      {/* Lo que todavía está pasando por ffmpeg o esperando turno. Va arriba porque es lo
          que se mueve solo: lo de abajo espera a que alguien decida. */}
      <PanelCola />

      {!bibliotecas.length && (
        <Alert severity="warning">
          <AlertTitle>No hay ninguna biblioteca a la que mover</AlertTitle>
          Una biblioteca solo sirve de destino si tiene <strong>ruta local</strong>: es la
          carpeta donde esta aplicación escribe. Se rellena en Ajustes → Servidores Plex, y
          conviene verificarla ahí mismo antes de mover nada.
        </Alert>
      )}

      <SelectorOrigen
        valor={pestana}
        onCambio={(v) => {
          setOrigen(v);
          // Lo marcado era de la otra pestaña: mover «las marcadas» desde aquí sería
          // mover lo que no se ve.
          setMarcados(new Set());
        }}
        cuentas={cuentas}
      />

      {items.length === 0 && (
        <Alert severity="info">
          {pestana === 'biblioteca'
            ? 'No hay nada pedido desde Biblioteca esperando en conversiones.'
            : 'No hay descargas esperando en conversiones. Aquí aparecerán en cuanto terminen de convertirse.'}
        </Alert>
      )}

      {/* Con una sola película que mover, una casilla y una barra de acciones sobran: se
          mueve desde su fila, que es donde está su ruta y sus avisos. */}
      {movibles.length > 1 && (
        <BarraSeleccion
          movibles={movibles}
          marcadas={marcadas}
          bibliotecas={bibliotecas}
          destino={destino}
          alElegirDestino={setDestinoMasivo}
          alMarcarTodas={(v) => setMarcados(v ? new Set(movibles.map((c) => c.downloadId)) : new Set())}
          alDejarSolo={(ids) => setMarcados(new Set(ids))}
          onCambio={refrescar}
        />
      )}

      {agruparConversiones(items).map((g) => {
        const fila = (c: Conversion) => (
          <FilaConversion
            key={c.downloadId}
            c={c}
            bibliotecas={bibliotecas}
            onCambio={refrescar}
            seleccion={
              movibles.length > 1 && esSeleccionable(c)
                ? {
                    marcada: marcados.has(c.downloadId),
                    alMarcar: (v) => marcar(c.downloadId, v),
                    destino,
                  }
                : null
            }
          />
        );
        if (g.tipo === 'suelta') return fila(g.c);
        const primera = g.items[0]!;
        return (
          <Stack key={`${primera.title}|${primera.year}|${primera.season ?? 'sin'}`} spacing={1.5}>
            <CabeceraTemporada
              items={g.items}
              alMarcar={
                movibles.length > 1
                  ? (ids) => setMarcados((antes) => new Set([...antes, ...ids]))
                  : null
              }
            />
            {g.items.map(fila)}
          </Stack>
        );
      })}
    </Stack>
  );
}
